/**
 * NewsDetail — a single archive entry with full Ford-style credits
 * (Photographed By / Styled By / Hair / Makeup) and a link to the talent.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";

const TYPE_LABEL = { campaign: "Campaign", editorial: "Editorial", cover: "Cover" };

export default function NewsDetail() {
  const { id } = useParams();
  const [work, setWork] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/latest/${id}`).then((r) => setWork(r.data)).catch(() => setError(true));
  }, [id]);

  usePageMeta(
    work ? `${work.brand}${work.model_name ? ` — ${work.model_name}` : ""} · Ege Models` : undefined,
    work ? `${TYPE_LABEL[work.type] || "Work"} by Ege Models talent${work.model_name ? ` ${work.model_name}` : ""}.` : undefined
  );

  if (error) {
    return (
      <div className="pt-40 pb-24 max-w-[1600px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500">404</p>
        <h1 className="font-serif text-6xl mt-4">Entry not found</h1>
        <Link to="/archive" className="btn-ghost mt-8">Back to the archive</Link>
      </div>
    );
  }
  if (!work) {
    return <div className="pt-40 pb-24 flex justify-center"><Loader2 size={20} className="animate-spin text-zinc-400"/></div>;
  }

  const credits = [
    ["Photographed by", work.photographer],
    ["Styled by", work.stylist],
    ["Hair by", work.hair],
    ["Makeup by", work.makeup],
  ].filter(([, v]) => v);

  const date = work.created_at ? new Date(work.created_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : null;

  return (
    <div data-testid="news-detail-page" className="pt-28 lg:pt-32 pb-24 bg-white min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Link to="/archive" data-testid="news-back" className="label-overline text-zinc-500 hover:text-black inline-flex items-center gap-2">
          <ArrowLeft size={13}/> The archive
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Image */}
          <div className="lg:col-span-7">
            <div className="relative bg-[#0A0A0A] overflow-hidden">
              <img
                src={work.image_url}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-30"
              />
              <img
                src={work.image_url}
                alt={`${work.brand}${work.subtitle ? " — " + work.subtitle : ""}`}
                data-testid="news-image"
                className="relative w-full h-auto max-h-[80vh] object-contain"
              />
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="label-overline text-zinc-500">
              {TYPE_LABEL[work.type] || "Work"}{date ? ` · ${date}` : ""}
            </p>
            <h1 data-testid="news-title" className="font-serif text-4xl md:text-5xl tracking-tighter font-light mt-4 leading-[0.95]">
              {work.brand}
            </h1>
            {work.subtitle && (
              <p className="font-serif text-xl md:text-2xl text-zinc-500 mt-2">{work.subtitle}</p>
            )}

            {work.model_name && (
              <div className="mt-8">
                <p className="label-overline text-zinc-400">Featuring</p>
                {work.model_slug ? (
                  <Link
                    to={`/models/${work.model_slug}`}
                    data-testid="news-model-link"
                    className="font-serif text-2xl mt-1 inline-block hover:text-zinc-500 transition-colors"
                  >
                    {work.model_name}
                  </Link>
                ) : (
                  <p className="font-serif text-2xl mt-1">{work.model_name}</p>
                )}
              </div>
            )}

            {credits.length > 0 && (
              <div className="mt-8 border-t border-black/10 pt-6 space-y-3" data-testid="news-credits">
                {credits.map(([label, value]) => (
                  <div key={label} className="flex items-baseline gap-4">
                    <p className="label-overline text-zinc-400 w-32 shrink-0">{label}</p>
                    <p className="font-serif text-lg">{value}</p>
                  </div>
                ))}
              </div>
            )}

            {work.caption && (
              <p className="mt-8 text-sm text-zinc-600 leading-relaxed">{work.caption}</p>
            )}

            {work.external_link && (
              <a
                href={work.external_link}
                target="_blank"
                rel="noreferrer"
                data-testid="news-external-link"
                className="btn-ghost mt-10 inline-flex items-center gap-2"
              >
                View the full story <ArrowUpRight size={14}/>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
